import { type ComponentType, type CSSProperties } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";

export interface TableActionOption<TValue extends string> {
  value: TValue;
  title: string;
  subtitle: string;
  icon: ComponentType<{
    className?: string;
    style?: CSSProperties;
  }>;
  section?: string;
}

interface TableActionModalProps<TValue extends string> {
  isOpen: boolean;
  isLoading: boolean;
  tableCount: number;
  onOpenChange: (open: boolean) => void;
  onSelect: (value: TValue) => void;
  title: string;
  description: string;
  footerLabel: string;
  loadingText: string;
  options: TableActionOption<TValue>[];
  headerIcon: ComponentType<{
    className?: string;
    style?: CSSProperties;
  }>;
}

const groupOptions = <TValue extends string>(
  options: TableActionOption<TValue>[]
) => {
  return options.reduce<
    Array<{ section: string; items: TableActionOption<TValue>[] }>
  >((acc, option) => {
    const sectionName = option.section ?? "";
    const section = acc.find((item) => item.section === sectionName);

    if (section) {
      section.items.push(option);
      return acc;
    }

    acc.push({ section: sectionName, items: [option] });
    return acc;
  }, []);
};

const TableActionModal = <TValue extends string>({
  isOpen,
  isLoading,
  tableCount,
  onOpenChange,
  onSelect,
  title,
  description,
  footerLabel,
  loadingText,
  options,
  headerIcon: HeaderIcon
}: TableActionModalProps<TValue>) => {
  const groupedOptions = groupOptions(options);

  const handleSelect = (value: TValue) => {
    if (!isLoading) {
      onSelect(value);
      // بعد از انتخاب، مدال رو ببند
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden p-0">
        <div className="w-full overflow-hidden bg-white">
          {/* Header */}
          <div className="relative border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white p-6 sm:p-8">
            <DialogHeader className="text-right">
              <DialogTitle className="flex items-center justify-end gap-3 text-xl font-bold text-slate-800">
                <span>{title}</span>
                <HeaderIcon className="h-6 w-6 text-teal-600" />
              </DialogTitle>
              <DialogDescription className="text-right text-sm text-slate-600">
                {description}
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Options Grid */}
          <div className="max-h-[50vh] overflow-y-auto p-4 sm:p-6">
            <div className="space-y-5">
              {groupedOptions.map((group, groupIndex) => (
                <div
                  key={`${group.section}-${groupIndex}`}
                  className="space-y-3"
                >
                  {group.section && (
                    <p className="border-r-2 border-teal-500 pr-2 text-right text-xs font-semibold text-slate-500">
                      {group.section}
                    </p>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    {group.items.map((option) => {
                      const Icon = option.icon;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={isLoading}
                          onClick={() => handleSelect(option.value)}
                          className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 text-right transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <div className="relative z-10 flex items-start justify-between gap-3 text-right">
                            <div className="rounded-lg border border-teal-100 bg-teal-50 p-2.5 transition-colors group-hover:bg-teal-100">
                              <Icon className="h-5 w-5 text-teal-600" />
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className="text-right text-sm font-semibold text-slate-800">
                                {option.title}
                              </p>
                              <p className="text-right text-xs leading-5 text-slate-500">
                                {option.subtitle}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-4 border-t border-slate-200 bg-slate-50 px-6 py-4 text-right text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-right text-slate-600">
                {tableCount} جدول
              </span>
              <span className="text-right">{footerLabel}</span>
            </div>
            {isLoading && (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
                <span>{loadingText}</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TableActionModal;
